import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, DollarSign, Link2, Copy, CheckCircle, 
  Clock, TrendingUp, Wallet, Send, UserPlus, Award, Star,
  BookOpen, Headphones, PenTool, MessageSquare, BarChart3, Target,
  ChevronDown, Zap, Calendar, Cookie, RefreshCw, CreditCard
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AffiliatePage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [affiliateData, setAffiliateData] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [copied, setCopied] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [counterValue, setCounterValue] = useState(50); // Start at 50 as fallback
  const [counterAnimated, setCounterAnimated] = useState(false);
  const formRef = useRef(null);
  
  // Registration form
  const [paymentMethod, setPaymentMethod] = useState('paypal');
  const [paymentDetails, setPaymentDetails] = useState('');

  // Scroll to form
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Counter animation - runs once on mount
  useEffect(() => {
    if (counterAnimated) return;
    
    // Small delay to ensure component is mounted, then animate
    const startAnimation = () => {
      setCounterAnimated(true);
      let start = 0;
      const target = 50;
      const duration = 1200;
      const steps = duration / 16;
      const increment = target / steps;
      
      const timer = setInterval(() => {
        start += increment;
        if (start >= target) {
          setCounterValue(50);
          clearInterval(timer);
        } else {
          setCounterValue(Math.floor(start));
        }
      }, 16);
      
      // Safety fallback - ensure it shows 50 after animation duration
      setTimeout(() => {
        setCounterValue(50);
      }, duration + 100);
    };
    
    // Start animation after a brief delay
    const timeout = setTimeout(startAnimation, 300);
    return () => clearTimeout(timeout);
  }, [counterAnimated]);

  useEffect(() => {
    checkAffiliateStatus();
  }, [token]);

  const checkAffiliateStatus = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/affiliates/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.is_affiliate) {
        setIsAffiliate(true);
        await loadAffiliateData();
      }
    } catch (error) {
      console.error('Error checking affiliate status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAffiliateData = async () => {
    try {
      const [dashboardRes, referralsRes, payoutsRes] = await Promise.all([
        axios.get(`${API}/affiliates/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/affiliates/referrals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/affiliates/payouts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setAffiliateData(dashboardRes.data);
      setReferrals(referralsRes.data.referrals || []);
      setPayouts(payoutsRes.data.payouts || []);
    } catch (error) {
      console.error('Error loading affiliate data:', error);
      toast.error('Error al cargar datos de afiliado');
    }
  };

  const registerAsAffiliate = async (e) => {
    e.preventDefault();
    
    if (!token) {
      toast.error('Please log in to register as an affiliate');
      navigate('/login');
      return;
    }
    
    if (!paymentDetails.trim()) {
      toast.error('Please enter your payment details');
      return;
    }
    
    setRegistering(true);
    try {
      const response = await axios.post(`${API}/affiliates/register`, {
        payment_method: paymentMethod,
        payment_details: paymentDetails
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAffiliateData(response.data);
      setIsAffiliate(true);
      toast.success('Welcome to the Affiliate Program!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error registering');
    } finally {
      setRegistering(false);
    }
  };

  const copyReferralLink = () => {
    if (affiliateData?.referral_link) {
      navigator.clipboard.writeText(affiliateData.referral_link);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const requestPayout = async () => {
    if (affiliateData?.balance < 20) {
      toast.error('Minimum payout is $20 USD');
      return;
    }
    
    setRequestingPayout(true);
    try {
      await axios.post(`${API}/affiliates/request-payout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Payout request submitted');
      await loadAffiliateData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error requesting payout');
    } finally {
      setRequestingPayout(false);
    }
  };

  const faqData = [
    { q: "Is it free to join?", a: "Yes, 100% free. No fees, no application costs, no minimum traffic requirements. There's nothing to pay — ever. Just sign up, get your link, and start sharing." },
    { q: "How do I get paid?", a: "Monthly, via PayPal or bank transfer. Once your balance reaches the minimum payout threshold of $20 USD, your earnings are automatically processed at the end of each month." },
    { q: "How long does the cookie last?", a: "90 days. If someone clicks your referral link and signs up at any point within those 90 days, you earn the commission — even if they don't subscribe immediately." },
    { q: "What if my students cancel their subscription?", a: "You earn commission for as long as the student maintains their active subscription. Students preparing for Cambridge exams typically stay subscribed for several months until their exam date." },
    { q: "Do I need technical skills to participate?", a: "Not at all. You get a simple unique referral link — share it anywhere: WhatsApp, email, Instagram, your class group, or your website. If you can send a message, you can be an affiliate." },
    { q: "Can I promote in Spanish?", a: "Absolutely — and we strongly encourage it. Exam Ready English is specifically designed for Spanish-speaking professionals preparing for Cambridge exams." }
  ];

  const benefits = [
    { icon: <Award className="w-7 h-7" />, title: "Your Students Pass", text: "Cambridge mock exam practice with AI feedback boosts pass rates. Happy students mean 5-star reviews for your teaching business." },
    { icon: <DollarSign className="w-7 h-7" />, title: "You Earn Generously", text: "50% recurring commission as a Founding Member. Real, meaningful income from every student you refer — month after month." },
    { icon: <Zap className="w-7 h-7" />, title: "Zero Extra Work", text: "We handle the platform, content, AI tutoring, and student support. You simply share your unique link with students." },
    { icon: <TrendingUp className="w-7 h-7" />, title: "Grow Your Reputation", text: "Students who use Exam Ready English get better results. Better results mean more referrals and a stronger teaching brand." }
  ];

  const platformFeatures = [
    { icon: <MessageSquare className="w-8 h-8" />, title: "AI Speaking Partner", text: "Jane simulates real Cambridge exam conversations — available 24/7." },
    { icon: <BookOpen className="w-8 h-8" />, title: "Reading & Use of English", text: "Authentic exam-style exercises mapped to Cambridge exam formats." },
    { icon: <PenTool className="w-8 h-8" />, title: "AI Writing Feedback", text: "Instant, detailed feedback on essays and tasks." },
    { icon: <Headphones className="w-8 h-8" />, title: "Listening Practice", text: "High-quality exercises that mirror real Cambridge exam audio." },
    { icon: <BarChart3 className="w-8 h-8" />, title: "Progress Tracking", text: "Detailed statistics so students can see their improvement." },
    { icon: <Target className="w-8 h-8" />, title: "100+ Practice Exercises", text: "Comprehensive library covering every section of Cambridge exams." }
  ];

  const testimonials = [
    { name: "María González", role: "Cambridge Exam Tutor, Madrid • B2 & C1 specialist", text: "I was skeptical at first — I'd tried other affiliate programs that paid peanuts. But with Exam Ready English, my first three B2 students subscribed within a week. That's over $40 in my first month doing absolutely nothing extra.", color: "from-[#2B4C7E] to-[#3a5f96]" },
    { name: "Carlos Romero", role: "Online ESL Teacher, Mexico City • C1 & C2 preparation", text: "What I love most is that my students actually pass. Four of my C1 students used Exam Ready English and three of them passed — that's an amazing rate. I'm now making an extra €180/month just from my existing students.", color: "from-[#D4A843] to-[#b8902e]" },
    { name: "Ana Patricia Vela", role: "Language Academy Instructor, Barcelona • B1 & B2", text: "I teach B1 adults and was looking for something to supplement my lessons. My students practise between sessions, come to class better prepared, and I earn commission on top. I've referred 12 students in two months.", color: "from-[#1a3057] to-[#2B4C7E]" }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Loading...</div>
      </div>
    );
  }

  // If user is already an affiliate, show the dashboard
  if (isAffiliate && token) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-slate-600">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Affiliate Dashboard</h1>
                <p className="text-sm text-slate-500">Earn 50% commission for every premium referral</p>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
          {/* Stats Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200 p-5 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Available Balance</p>
                  <p className="text-2xl font-bold text-slate-900">${affiliateData?.balance?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-slate-200 p-5 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Earned</p>
                  <p className="text-2xl font-bold text-slate-900">${affiliateData?.total_earned?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-slate-200 p-5 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Referrals</p>
                  <p className="text-2xl font-bold text-slate-900">{affiliateData?.total_referrals || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-slate-200 p-5 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Converted</p>
                  <p className="text-2xl font-bold text-slate-900">{affiliateData?.converted_referrals || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Referral Link */}
          <div className="bg-white border border-slate-200 p-6 rounded-lg">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Link2 className="w-5 h-5 text-[#D4A843]" />
              Your Referral Link
            </h3>
            <div className="flex gap-2">
              <Input value={affiliateData?.referral_link || ''} readOnly className="font-mono text-sm bg-slate-50" />
              <Button onClick={copyReferralLink} variant="outline" className={copied ? 'bg-green-50 border-green-300 text-green-600' : ''}>
                {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-sm text-slate-500 mt-2">Share this link. When someone registers and subscribes to Premium, you earn 50% commission.</p>
          </div>

          {/* Request Payout */}
          <div className="bg-white border border-slate-200 p-6 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Send className="w-5 h-5 text-[#D4A843]" />
                  Request Payout
                </h3>
                <p className="text-sm text-slate-500 mt-1">Minimum $20 USD. Method: {affiliateData?.payment_method === 'paypal' ? 'PayPal' : 'Bank Transfer'}</p>
              </div>
              <Button onClick={requestPayout} disabled={requestingPayout || (affiliateData?.balance || 0) < 20} className="bg-[#2B4C7E] hover:bg-[#1a3057] disabled:opacity-50">
                {requestingPayout ? 'Requesting...' : `Request $${affiliateData?.balance?.toFixed(2) || '0.00'}`}
              </Button>
            </div>
          </div>

          {/* Recent Referrals */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-[#D4A843]" />
                Recent Referrals
              </h3>
            </div>
            {referrals.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No referrals yet</p>
                <p className="text-sm">Share your link to start earning!</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {referrals.slice(0, 10).map((referral) => (
                  <div key={referral.id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{referral.referred_email}</p>
                      <p className="text-sm text-slate-500">{new Date(referral.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${referral.status === 'converted' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {referral.status === 'converted' ? <><CheckCircle className="w-3 h-3" />${referral.commission_amount?.toFixed(2)}</> : <><Clock className="w-3 h-3" />Pending</>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Payout History */}
          {payouts.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#D4A843]" />
                  Payout History
                </h3>
              </div>
              <div className="divide-y divide-slate-100">
                {payouts.map((payout) => (
                  <div key={payout.id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">${payout.amount?.toFixed(2)}</p>
                      <p className="text-sm text-slate-500">{new Date(payout.requested_at).toLocaleDateString()}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${payout.status === 'paid' ? 'bg-green-100 text-green-700' : payout.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                      {payout.status === 'paid' ? 'Paid' : payout.status === 'pending' ? 'Pending' : 'Rejected'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  // Landing page for non-affiliates
  return (
    <div className="min-h-screen" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      {/* HERO SECTION */}
      <section className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 py-16 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a3057 0%, #2B4C7E 50%, #3a5f96 100%)' }}>
        {/* Background effects */}
        <div className="absolute top-[-50%] left-[-20%] w-[600px] h-[600px] rounded-full opacity-30" style={{ background: 'radial-gradient(circle, rgba(212,168,67,0.12) 0%, transparent 70%)' }} />
        <div className="absolute bottom-[-30%] right-[-10%] w-[500px] h-[500px] rounded-full opacity-30" style={{ background: 'radial-gradient(circle, rgba(100,140,200,0.15) 0%, transparent 70%)' }} />
        
        <div className="relative z-10 max-w-[820px]">
          <img src="https://customer-assets.emergentagent.com/job_spanenglishprep/artifacts/i0n29hu3_Exam%20Ready%20English%20Logo.png" alt="Exam Ready English Logo" className="h-[70px] mx-auto mb-10 drop-shadow-lg" />
          
          <div className="inline-flex items-center gap-2 bg-[rgba(212,168,67,0.2)] border border-[rgba(212,168,67,0.4)] rounded-full px-5 py-2 text-[#e8c060] text-sm font-semibold mb-7">
            <span className="w-2 h-2 bg-[#D4A843] rounded-full animate-pulse" />
            Affiliate Program — Founding Member Access
          </div>

          <div className="flex items-baseline justify-center gap-1 mb-2">
            <span className="text-[clamp(56px,9vw,96px)] font-black text-[#D4A843] leading-none tabular-nums">{counterValue}</span>
            <span className="text-[clamp(28px,4vw,48px)] font-extrabold text-[#e8c060]">%</span>
          </div>
          <div className="text-sm text-white/70 uppercase tracking-wider font-semibold mb-6">Founding Member Commission Rate</div>

          <h1 className="text-[clamp(32px,5.5vw,58px)] font-black text-white leading-[1.1] mb-5 tracking-tight">
            Turn Your Teaching Expertise<br />Into <span className="bg-gradient-to-r from-[#e8c060] to-[#D4A843] bg-clip-text text-transparent">Recurring Revenue</span>
          </h1>

          <p className="text-[clamp(16px,2.5vw,20px)] text-white/80 max-w-[620px] mx-auto mb-10 leading-relaxed">
            Join the Exam Ready English Affiliate Program and earn up to 50% commission helping your students pass their Cambridge exams — without any extra teaching work.
          </p>

          <div className="flex flex-wrap gap-4 justify-center mb-12">
            <button onClick={scrollToForm} className="inline-flex items-center justify-center gap-2 font-bold text-lg px-12 py-5 rounded-full text-white transition-all hover:translate-y-[-2px] hover:scale-[1.02]" style={{ background: 'linear-gradient(135deg, #D4A843 0%, #b8902e 100%)', boxShadow: '0 4px 20px rgba(212,168,67,0.35)' }}>
              Become a Founding Member
            </button>
          </div>

          <div className="flex items-center justify-center gap-3 flex-wrap">
            <div className="text-[#D4A843] text-base tracking-widest">★★★★★</div>
            <span className="text-white/70 text-sm font-medium">Trusted by ESL teachers worldwide</span>
          </div>

          <img src="https://customer-assets.emergentagent.com/job_spanenglishprep/artifacts/795ln30m_exam%20ready%20english%20cover.png" alt="Exam Ready English Platform" className="mt-16 max-w-[700px] w-full rounded-2xl shadow-2xl border border-white/10" />
        </div>
      </section>

      {/* BENEFITS SECTION */}
      <section className="py-24 bg-[#f8f9fc]">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block bg-[rgba(212,168,67,0.15)] text-[#b8902e] text-xs font-bold tracking-wider uppercase px-4 py-1.5 rounded-full mb-4">Why Teachers Love This</span>
            <h2 className="text-[clamp(28px,4vw,42px)] font-extrabold text-[#1a2340] leading-[1.2] mb-4">Everything You Need.<br />Nothing You Don't.</h2>
            <p className="text-lg text-[#6b7a9e] max-w-[600px] mx-auto">We designed this program specifically for Cambridge exam teachers who want to add a meaningful income stream without the hassle.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-7">
            {benefits.map((b, i) => (
              <div key={i} className="bg-white rounded-2xl p-9 shadow-md border border-[#e2e6ef] hover:translate-y-[-6px] hover:shadow-xl transition-all duration-300 relative overflow-hidden group">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2B4C7E] to-[#D4A843] opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="w-[60px] h-[60px] bg-gradient-to-br from-[rgba(43,76,126,0.1)] to-[rgba(43,76,126,0.06)] rounded-xl flex items-center justify-center mb-6 text-[#2B4C7E]">{b.icon}</div>
                <h3 className="text-xl font-bold text-[#1a2340] mb-3">{b.title}</h3>
                <p className="text-[15px] text-[#6b7a9e] leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-white">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block bg-[rgba(212,168,67,0.15)] text-[#b8902e] text-xs font-bold tracking-wider uppercase px-4 py-1.5 rounded-full mb-4">Simple Process</span>
            <h2 className="text-[clamp(28px,4vw,42px)] font-extrabold text-[#1a2340] leading-[1.2] mb-4">Start Earning in 3 Easy Steps</h2>
            <p className="text-lg text-[#6b7a9e] max-w-[600px] mx-auto">No technical skills required. No complex setup. Just sign up, share, and earn.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { num: 1, title: "Sign Up Free", text: "Create your affiliate account in 60 seconds. Get your unique referral link instantly — no approval wait, no upfront costs." },
              { num: 2, title: "Share With Students", text: "Recommend Exam Ready English to your Cambridge exam students. Share via email, social media, WhatsApp, or directly in your classroom." },
              { num: 3, title: "Earn Commission", text: "Every time a student subscribes through your link, you earn up to 50% commission. Paid monthly, tracked automatically." }
            ].map((s, i) => (
              <div key={i} className="text-center p-10 bg-white rounded-3xl shadow-md border border-[#e2e6ef] hover:translate-y-[-4px] hover:shadow-xl transition-all">
                <div className="w-[72px] h-[72px] mx-auto mb-6 rounded-full flex items-center justify-center text-3xl font-black text-white" style={{ background: 'linear-gradient(135deg, #2B4C7E, #3a5f96)', boxShadow: '0 8px 24px rgba(43,76,126,0.3)' }}>{s.num}</div>
                <h3 className="text-[22px] font-bold text-[#1a2340] mb-3">{s.title}</h3>
                <p className="text-[15px] text-[#6b7a9e] leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMISSION STRUCTURE */}
      <section className="py-24 bg-[#f8f9fc]">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block bg-[rgba(212,168,67,0.15)] text-[#b8902e] text-xs font-bold tracking-wider uppercase px-4 py-1.5 rounded-full mb-4">Commission Structure</span>
            <h2 className="text-[clamp(28px,4vw,42px)] font-extrabold text-[#1a2340] leading-[1.2] mb-4">Lock In Your Rate<br />Before It's Gone</h2>
            <p className="text-lg text-[#6b7a9e] max-w-[600px] mx-auto">Founding Members get significantly better commissions. But this tier won't be open forever.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-7 max-w-[800px] mx-auto">
            {/* Standard Card */}
            <div className="bg-white rounded-3xl p-12 text-center border-2 border-[#e2e6ef] shadow-sm">
              <p className="text-sm font-semibold uppercase tracking-wider text-[#5a6480] mb-4">Standard Rate</p>
              <div className="text-[clamp(56px,8vw,80px)] font-black text-[#5a6480] leading-none mb-2">30%</div>
              <h3 className="text-xl font-bold text-[#1a2340] mb-5">Standard Member</h3>
              <ul className="text-left text-sm text-[#3d4a6b] space-y-2.5">
                {["30% recurring commission", "Monthly payouts", "90-day cookie window", "Affiliate dashboard", "Marketing materials"].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5"><span className="text-[#2B4C7E] font-bold mt-0.5">✓</span>{f}</li>
                ))}
              </ul>
            </div>

            {/* Founding Card */}
            <div className="relative rounded-3xl p-12 text-center text-white border-2 border-[#D4A843] transform md:scale-[1.04] hover:scale-[1.06] hover:translate-y-[-4px] transition-all" style={{ background: 'linear-gradient(135deg, #1a3057 0%, #2B4C7E 100%)', boxShadow: '0 20px 60px rgba(43,76,126,0.18), 0 0 40px rgba(212,168,67,0.2)' }}>
              <div className="absolute top-5 right-[-8px] text-xs font-extrabold tracking-wider uppercase px-4 py-1.5 rounded text-white" style={{ background: 'linear-gradient(135deg, #D4A843, #b8902e)', boxShadow: '0 4px 12px rgba(212,168,67,0.4)' }}>
                <Zap className="w-3 h-3 inline mr-1" />Limited Time
              </div>
              <p className="text-sm font-semibold uppercase tracking-wider text-white/70 mb-4">Founding Member Rate</p>
              <div className="text-[clamp(56px,8vw,80px)] font-black text-[#D4A843] leading-none mb-2">50%</div>
              <h3 className="text-xl font-bold mb-5">Founding Member</h3>
              <ul className="text-left text-sm text-white/85 space-y-2.5">
                {["50% recurring commission", "Priority monthly payouts", "90-day cookie window", "Dedicated affiliate dashboard", "Premium marketing kit", "Locked in for life"].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5"><span className="text-[#D4A843] font-bold mt-0.5">✓</span>{f}</li>
                ))}
              </ul>
              <button onClick={scrollToForm} className="w-full mt-7 py-4 rounded-full font-bold text-white transition-all hover:translate-y-[-2px]" style={{ background: 'linear-gradient(135deg, #D4A843 0%, #b8902e 100%)' }}>
                Claim My 50% Rate →
              </button>
            </div>
          </div>

          <div className="bg-gradient-to-r from-[rgba(212,168,67,0.1)] to-[rgba(212,168,67,0.05)] border border-[rgba(212,168,67,0.3)] rounded-xl p-6 mt-12 max-w-[700px] mx-auto text-center">
            <p className="text-[#3d4a6b] text-[15px] leading-relaxed">
              ⏳ <strong className="text-[#b8902e]">The Founding Member rate of 50% is available for a limited time only.</strong> Once we close this tier, the standard rate will be 30%. That's a 20 percentage point difference on every referral, every month. <strong className="text-[#b8902e]">Lock in your 50% rate today.</strong>
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-12 max-w-[800px] mx-auto">
            {[
              { icon: <Calendar className="w-7 h-7" />, label: "Payout Schedule", value: "Monthly" },
              { icon: <Cookie className="w-7 h-7" />, label: "Cookie Duration", value: "90 Days" },
              { icon: <RefreshCw className="w-7 h-7" />, label: "Commission Type", value: "Recurring" },
              { icon: <CreditCard className="w-7 h-7" />, label: "Min. Payout", value: "$20 USD" }
            ].map((e, i) => (
              <div key={i} className="bg-white rounded-xl p-5 text-center shadow-sm border border-[#e2e6ef]">
                <div className="text-[#2B4C7E] mb-2 flex justify-center">{e.icon}</div>
                <div className="text-xs uppercase tracking-wider text-[#6b7a9e] mb-1">{e.label}</div>
                <div className="text-base font-bold text-[#1a2340]">{e.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLATFORM VALUE */}
      <section className="py-24 bg-white">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block bg-[rgba(212,168,67,0.15)] text-[#b8902e] text-xs font-bold tracking-wider uppercase px-4 py-1.5 rounded-full mb-4">Platform Value</span>
            <h2 className="text-[clamp(28px,4vw,42px)] font-extrabold text-[#1a2340] leading-[1.2] mb-4">What Your Students Get</h2>
            <p className="text-lg text-[#6b7a9e] max-w-[600px] mx-auto">You can recommend Exam Ready English with total confidence.</p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {["B1 Preliminary", "B2 First", "C1 Advanced", "C2 Proficiency"].map((l, i) => (
              <span key={i} className="text-sm font-bold text-white px-6 py-2.5 rounded-full" style={{ background: 'linear-gradient(135deg, #2B4C7E, #3a5f96)', boxShadow: '0 4px 16px rgba(43,76,126,0.25)' }}>{l}</span>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {platformFeatures.map((f, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 shadow-sm border border-[#e2e6ef] text-center hover:translate-y-[-4px] hover:shadow-md transition-all">
                <div className="text-[#2B4C7E] mb-4 flex justify-center">{f.icon}</div>
                <div className="text-base font-bold text-[#1a2340] mb-2">{f.title}</div>
                <div className="text-sm text-[#6b7a9e] leading-relaxed">{f.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-[#f8f9fc]">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block bg-[rgba(212,168,67,0.15)] text-[#b8902e] text-xs font-bold tracking-wider uppercase px-4 py-1.5 rounded-full mb-4">Teacher Stories</span>
            <h2 className="text-[clamp(28px,4vw,42px)] font-extrabold text-[#1a2340] leading-[1.2] mb-4">Teachers Are Already Earning</h2>
            <p className="text-lg text-[#6b7a9e] max-w-[600px] mx-auto">Real experiences from ESL teachers who share Exam Ready English with their students.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-3xl p-9 shadow-md border border-[#e2e6ef] relative hover:translate-y-[-4px] hover:shadow-xl transition-all">
                <div className="absolute top-4 left-7 text-7xl text-[#D4A843] opacity-30 font-serif leading-none">"</div>
                <p className="text-[15px] text-[#3d4a6b] leading-relaxed mb-6 pt-5 relative z-10">{t.text}</p>
                <div className="flex items-center gap-3.5">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold text-white bg-gradient-to-br ${t.color}`}>{t.name[0]}</div>
                  <div>
                    <div className="text-[15px] font-bold text-[#1a2340]">{t.name}</div>
                    <div className="text-xs text-[#6b7a9e] mt-0.5">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center mt-6 text-xs text-[#9aa3b8]">* Testimonials are illustrative of typical experiences. Individual results may vary.</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block bg-[rgba(212,168,67,0.15)] text-[#b8902e] text-xs font-bold tracking-wider uppercase px-4 py-1.5 rounded-full mb-4">Got Questions?</span>
            <h2 className="text-[clamp(28px,4vw,42px)] font-extrabold text-[#1a2340] leading-[1.2] mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-[#6b7a9e] max-w-[600px] mx-auto">Everything you need to know about the affiliate program — answered clearly.</p>
          </div>

          <div className="max-w-[760px] mx-auto space-y-3">
            {faqData.map((faq, i) => (
              <div key={i} className={`bg-white rounded-xl border ${openFaq === i ? 'border-[#2B4C7E] shadow-md' : 'border-[#e2e6ef] shadow-sm'} overflow-hidden transition-all`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full p-5 text-left flex items-center justify-between gap-3 text-base font-semibold text-[#1a2340] hover:text-[#2B4C7E] transition-colors">
                  <span>{faq.q}</span>
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-lg transition-all ${openFaq === i ? 'bg-[#2B4C7E] text-white rotate-45' : 'bg-[#f1f3f8] text-[#2B4C7E]'}`}>+</span>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-[300px]' : 'max-h-0'}`}>
                  <div className="px-5 pb-5 text-[15px] text-[#6b7a9e] leading-relaxed">{faq.a}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SIGNUP FORM SECTION */}
      <section ref={formRef} id="signup" className="py-24 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a3057 0%, #2B4C7E 60%, #3a5f96 100%)' }}>
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }} />
        
        <div className="max-w-[600px] mx-auto px-6 relative z-10">
          <div className="text-center mb-10">
            <h2 className="text-[clamp(32px,5vw,52px)] font-black text-white mb-4 leading-[1.15]">Ready to Start Earning?</h2>
            <p className="text-lg text-white/75 max-w-[500px] mx-auto">Lock in your 50% Founding Member commission rate before this tier closes. Join free in under 60 seconds.</p>
          </div>

          {/* Registration Form */}
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #D4A843, #b8902e)' }}>
                <Award className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#1a2340]">Become a Founding Member</h3>
                <p className="text-sm text-[#6b7a9e]">Lock in 50% commission for life</p>
              </div>
            </div>

            {!token ? (
              <div className="text-center py-8">
                <p className="text-[#3d4a6b] mb-4">Please log in or create an account to register as an affiliate.</p>
                <div className="flex gap-3 justify-center">
                  <Button onClick={() => navigate('/login')} className="bg-[#2B4C7E] hover:bg-[#1a3057] text-white">
                    Log In
                  </Button>
                  <Button onClick={() => navigate('/register')} variant="outline" className="border-[#2B4C7E] text-[#2B4C7E]">
                    Create Account
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={registerAsAffiliate} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#1a2340] mb-2">Payment Method</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" value="paypal" checked={paymentMethod === 'paypal'} onChange={(e) => setPaymentMethod(e.target.value)} className="accent-[#D4A843]" />
                      <span className="text-sm text-[#3d4a6b]">PayPal</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" value="bank_transfer" checked={paymentMethod === 'bank_transfer'} onChange={(e) => setPaymentMethod(e.target.value)} className="accent-[#D4A843]" />
                      <span className="text-sm text-[#3d4a6b]">Bank Transfer</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1a2340] mb-2">
                    {paymentMethod === 'paypal' ? 'PayPal Email' : 'IBAN / Bank Details'}
                  </label>
                  <Input
                    type={paymentMethod === 'paypal' ? 'email' : 'text'}
                    value={paymentDetails}
                    onChange={(e) => setPaymentDetails(e.target.value)}
                    placeholder={paymentMethod === 'paypal' ? 'your@email.com' : 'ES00 0000 0000 0000 0000 0000'}
                    required
                    className="border-[#e2e6ef] focus:border-[#D4A843] focus:ring-[#D4A843]"
                  />
                </div>

                <Button type="submit" disabled={registering} className="w-full py-6 text-lg font-bold rounded-full text-white" style={{ background: 'linear-gradient(135deg, #D4A843 0%, #b8902e 100%)' }}>
                  {registering ? 'Registering...' : 'Join the Affiliate Program Now →'}
                </Button>
              </form>
            )}
          </div>

          <div className="flex justify-center items-center gap-4 flex-wrap mt-6 text-sm text-white/55">
            <span className="flex items-center gap-1.5"><span className="text-[#D4A843] font-bold">✓</span>Free to join</span>
            <span className="flex items-center gap-1.5"><span className="text-[#D4A843] font-bold">✓</span>No commitment</span>
            <span className="flex items-center gap-1.5"><span className="text-[#D4A843] font-bold">✓</span>Start earning immediately</span>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#2d3452] py-8 text-center">
        <div className="max-w-[1100px] mx-auto px-6">
          <img src="https://customer-assets.emergentagent.com/job_spanenglishprep/artifacts/i0n29hu3_Exam%20Ready%20English%20Logo.png" alt="Exam Ready English" className="h-10 mx-auto mb-4 opacity-70" />
          <p className="text-white/50 text-sm leading-relaxed">
            © 2024 Exam Ready English — Part of <a href="https://english4you.app" target="_blank" rel="noopener noreferrer" className="text-white/70 underline">english4you.app</a><br />
            Helping Spanish-speaking professionals succeed in their Cambridge English exams.<br />
            <span className="text-xs opacity-60">Questions about the affiliate program? Contact us at the platform.</span>
          </p>
        </div>
      </footer>
    </div>
  );
}
